"use client";

import { useCallback, useState } from "react";

/**
 * Manages a step's local sub-phase state machine: current phase, its slide-animation direction, and
 * back-chain navigation derived from `phases`' order — rather than a hand-written if/else per step,
 * so reordering a step's screens is a one-line array change instead of an edit to N places.
 * Shared across every wizard step that chains multiple screens via local `subPhase` state.
 *
 * @param phases - Ordered phase ids for this step.
 * @param initial - Starting phase (not necessarily `phases[0]` — e.g. a step may resume further in
 *   when its data already answers an earlier screen).
 * @param onExitFirst - Called instead of stepping back when already on `phases[0]` — typically the
 *   wizard-level `onBack`.
 * @returns `{ phase, direction, goTo, goBack }`.
 */
export function useSubPhase<T extends string>(
  phases: readonly T[],
  initial: T,
  onExitFirst?: () => void
): { phase: T; direction: 1 | -1; goTo: (next: T) => void; goBack: () => void } {
  const [phase, setPhase] = useState<T>(initial);
  const [direction, setDirection] = useState<1 | -1>(1);

  const goTo = useCallback(
    (next: T) => {
      setDirection(phases.indexOf(next) >= phases.indexOf(phase) ? 1 : -1);
      setPhase(next);
    },
    [phase, phases]
  );

  const goBack = useCallback(() => {
    const index = phases.indexOf(phase);
    if (index <= 0) {
      onExitFirst?.();
      return;
    }
    setDirection(-1);
    setPhase(phases[index - 1]);
  }, [phase, phases, onExitFirst]);

  return { phase, direction, goTo, goBack } as const;
}
